import { cx } from "@emotion/css";
import { useMemo, useState } from "react";

import { axiosInstance } from "@/configs/axiosConfig";
import { dnbComLog, isNotUndefined, LogSeverity, trackGTMSearchSubmitEvents } from "@/shared/FunctionCore";
import { classCombinerFromPrefix } from "@/app/pages/upik/resources/RenderIdentifier";
import { tooltipContent } from "@/app/pages/upik/resources/content";
import { COUNTRIES } from "@/app/pages/upik/resources/countryList";
import infoIconGray from "@/app/pages/upik/resources/infoIconGray.svg";
import { PageUrlProps, SearchFormTabProps } from "@/app/pages/upik/resources/interfacesData";
import * as SearchTabsStyles from "@/app/pages/upik/styles/SearchTabsStyles.module.scss";
import { BottomContent } from "@/app/pages/upik/components/BottomContent";
import { PrivateMsgBox } from "@/app/pages/upik/components/PrivateMsgBox";
import { SearchResult } from "@/app/pages/upik/components/SearchResult";
import { CustomizedTooltips } from "@/app/pages/upik/components/Tooltip";
import React from "react";

const styles = SearchTabsStyles;
const classCombiner = classCombinerFromPrefix("6e1124ea8c05f948");

type PropTypes = PageUrlProps & SearchFormTabProps;

const SearchFormTab = (props: PropTypes): JSX.Element => {
    const [searchTerm, setSearchTerm] = useState("");
    const [countryCode, setCountryCode] = useState("DE");
    const [locationData, setLocationData] = useState("");
    const [streetAddress, setStreetAddress] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const [companiesData, setCompaniesData] = useState([]);
    const [noCompaniesFoundMessage, setNoCompaniesFoundMessage] = useState("");
    const [openTooltip, setOpenTooltip] = useState(false);
    const firstHeading = "UPIK<sup>®</sup> - Unique Partner Identification Key";
    const secondHeading = props.language === 'de' ? "Suchen Sie nach Ihrem Unternehmen" : "Search for your company";
    const thirdHeading = props.language === 'de' ? "Bitte geben Sie den genauen Firmennamen oder die D-U-N-S-Nummer des Unternehmens ein, das Sie suchen." : "Please enter an exact name or D-U-N-S Number of the company you are looking for.";
    const searchLabel = props.language === 'de' ? "Suche" : "Search";
    const clearSearchLabel = props.language === 'de' ? "Suche löschen" : "Clear Search";
    const searchHerePlaceholder = props.language === 'de' ? "Suche hier..." : "Search here...";
    const cityPlaceholder = props.language === 'de' ? "Stadt" : "City";
    const streetPlaceholder = props.language === 'de' ? "Straße" : "Street";
    const noDataFoundMsg = props.language === 'de' ? "Keine Ergebnisse." : "No results.";

    const searchUpik = (ev: React.SyntheticEvent) => {
        ev.preventDefault();
        setCompaniesData([]);
        setNoCompaniesFoundMessage("");
        setShowLoader(true);

        axiosInstance.get(`cleansematch`, {
            "params": {
                "countrycode": countryCode,
                "location": locationData,
                "searchterm": searchTerm.trim(),
                "streetaddress": streetAddress
            },
        }).then((ax) => {
            setShowLoader(false);
            if (ax.data.companies.length > 0) {
                setCompaniesData(ax.data.companies);
            } else {
                setNoCompaniesFoundMessage(props.noDataFoundMsgLocalized ?? noDataFoundMsg);
            }
        }).catch((error) => {
            setShowLoader(false);
            dnbComLog(LogSeverity.ERROR, error);
            setNoCompaniesFoundMessage(props.noDataFoundMsgLocalized ?? noDataFoundMsg);
        });

        if (isNotUndefined(searchTerm)) {
            trackGTMSearchSubmitEvents(searchTerm);
        }
    };

    const resetUpikFormData = () => {
        setNoCompaniesFoundMessage("");
        setSearchTerm("");
        setCountryCode("DE");
        setLocationData("");
        setStreetAddress("");
        setCompaniesData([]);
    };

    const countryListLanguage = props.language === "en" ? "COUNTRIES_IN_ENGLISH" : "COUNTRIES_IN_GERMAN";

    const infoIconContent = useMemo(() => {
        if (isNotUndefined(props.infoContentLocalizedPara1)) {
            return [
                props.infoContentLocalizedPara1,
                props.infoContentLocalizedPara2,
                props.infoContentLocalizedPara3
            ];
        }

        return props.language === "de" ? tooltipContent["de"] : tooltipContent["en"];
    }, [props.infoContentLocalizedPara1, props.infoContentLocalizedPara2, props.infoContentLocalizedPara3]);

    return (
        <div
            onClick={
                () => {
                    setOpenTooltip(false);
                }
            }
        >
            <div className={cx(classCombiner(styles, "tabContentContainer"))}>
                {isNotUndefined(props.isPrivatePage) && props.isPrivatePage ?
                    <div className={cx(classCombiner(styles, "msgBoxContainer"))}>
                        <PrivateMsgBox
                            privateMsgLocalized={props.privateMsgLocalized}
                            privateMsgNoteLocalized={props.privateMsgNoteLocalized}
                        />
                    </div> : <></>}

                {showLoader ? <div className={cx(classCombiner(styles, "loaderGifOuterWrapper"))}>
                    <div className={cx(classCombiner(styles, "loaderGifWrapper"))}>
                        <div className={cx(classCombiner(styles, "loaderGif"))} />
                    </div>
                </div> :
                    <>
                        <div>
                            <div className={cx(classCombiner(styles, "displayFlex"))}>
                                <div className={cx(classCombiner(styles, "alignCenterFontBold"))}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            "__html": props.uniquePartnerTitleLocalized ?? firstHeading
                                        }}
                                    />

                                    <CustomizedTooltips
                                        content={
                                            <>
                                                {infoIconContent.map((paragraph, index) => (
                                                    <p key={`$tooltippara-${index}}`}>
                                                        {paragraph}
                                                    </p>
                                                ))}
                                            </>
                                        }
                                        openTooltip={openTooltip}
                                        toggleTooltip={setOpenTooltip}
                                    >
                                        <a
                                            className={cx(classCombiner(styles, "infoIcon"))}
                                            onClick={
                                                (e) => {
                                                    e.stopPropagation();
                                                    setOpenTooltip(!openTooltip);
                                                }
                                            }
                                        >
                                            <img
                                                alt="info icon"
                                                src={`${infoIconGray}`}
                                            />
                                        </a>
                                    </CustomizedTooltips>
                                </div>
                            </div>

                            <div className={cx(classCombiner(styles, "displayFlex"))}>
                                <div className={cx(classCombiner(styles, "searchForYourCompanyClass"))}>
                                    {props.searchForCompanyLocalized ?? secondHeading}
                                </div>
                            </div>

                            <div className={cx(classCombiner(styles, "displayFlex"))}>
                                <div className={cx(classCombiner(styles, "plsEnterSubHeadingClass"))}>
                                    {props.formInstructionLocalized ?? thirdHeading}
                                </div>
                            </div>
                        </div>

                        <div className={cx(classCombiner(styles, "upikSearchFormFieldsContainer"))}>
                            <form onSubmit={searchUpik}>
                                <div className={cx(classCombiner(styles, "displayFlex"))}>
                                    <input
                                        className={cx(classCombiner(styles, "searchUpikInputBox"))}
                                        onChange={(data) => {
                                            setSearchTerm(data.target.value);
                                        }}
                                        placeholder={props.searchHereLocalized ?? searchHerePlaceholder}
                                        required={true}
                                        type="text"
                                        value={searchTerm} />
                                </div>

                                <div className={cx(classCombiner(styles, "displayFlex"))}>
                                    <select
                                        className={cx(classCombiner(styles, "upikCountriesDropdown"), classCombiner(styles, "arrow"))}
                                        id="country"
                                        name="country"
                                        onChange={(e) => setCountryCode(e.target.value)}
                                        value={countryCode}
                                    >
                                        {Object.entries(COUNTRIES[countryListLanguage]).map((t, k) =>
                                            <option
                                                disabled={t[0] === "" ? true : false}
                                                key={k}
                                                value={t[0]}>
                                                {t[1]}
                                            </option>)}
                                    </select>

                                    <input
                                        className={cx(classCombiner(styles, "streetLocationInputBox"))}
                                        onChange={(data) => {
                                            setStreetAddress(data.target.value);
                                        }}
                                        placeholder={props.streetLocalized ?? streetPlaceholder}
                                        type="text"
                                        value={streetAddress}
                                    />

                                    <input
                                        className={cx(classCombiner(styles, "streetLocationInputBox"))}
                                        onChange={(data) => {
                                            setLocationData(data.target.value);
                                        }}
                                        placeholder={props.cityLocalized ?? cityPlaceholder}
                                        type="text"
                                        value={locationData}
                                    />
                                </div>

                                <div className={cx(classCombiner(styles, "displayFlex"))}>
                                    <div className={cx(classCombiner(styles, "upikSearchButtonContainer"))}>
                                        <button
                                            className={cx(classCombiner(styles, "clearSearch"))}
                                            onClick={resetUpikFormData}
                                            type="button">
                                            {props.clearSearchLocalized ?? clearSearchLabel}
                                        </button>

                                        <button
                                            className={cx(classCombiner(styles, "searchBtn"))}
                                            type="submit">
                                            {" "}

                                            {props.searchButtonLocalized ?? searchLabel}

                                            {" "}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </>}
            </div>

            <div>
                <div className={cx(classCombiner(styles, "noCompaniesFound"))}>
                    {noCompaniesFoundMessage}
                </div>

                {companiesData.length > 0 ?
                    <SearchResult
                        companiesAPIdata={companiesData}
                        companyAddressLocalized={props.companyAddressLocalized}
                        companyResultNoteLocalized={props.companyResultNoteLocalized}
                        dunsNumberLocalized={props.dunsNumberLocalized}
                        editDetailsLocalized={props.editDetailsLocalized}
                        hereRedirectionLinkUrl={props.hereRedirectionLinkUrl}
                        isPrivatePage={props.isPrivatePage}
                        searchResultsLocalized={props.searchResultsLocalized}
                        topLocalized={props.topLocalized}
                        language={props.language}
                    /> : <></>}

                <BottomContent
                    language={props.language}
                    isPrivatePage={props.isPrivatePage}
                    bottomContentParaOneLocalized={props.bottomContentParaOneLocalized}
                    bottomContentParaTwoLocalized={props.bottomContentParaTwoLocalized}
                    hereRedirectionLinkUrl={props.hereRedirectionLinkUrl}
                />
            </div>
        </div>
    );
};

export { SearchFormTab };
